"use client"

import { useEffect, useState } from "react"
import { getAuthToken } from "../../../utils/authStorage"
import { API_BASE_URL as API_BASE } from "../../../config/api"
import { useAuth } from "../../../contexts/AuthContext"
import { useTheme } from "../../../contexts/ThemeContext"

const TRIGGER_TYPES = [
  { value: "contains", label: "Message contains" },
  { value: "equals", label: "Message equals" },
  { value: "starts_with", label: "Message starts with" },
  { value: "always", label: "Always (fallback)" },
]

export default function Bot() {
  const { user } = useAuth()
  const { isDark } = useTheme()
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingRule, setEditingRule] = useState(null)
  const [form, setForm] = useState({
    name: "",
    order: 0,
    isActive: true,
    triggerType: "contains",
    triggerText: "",
    responseText: "Hello {{name}}, welcome!",
  })

  const token = getAuthToken()

  const authHeaders = token
    ? {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" }

  // ------- API calls -------

  const loadRules = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_BASE}/api/bot/rules`, {
        headers: authHeaders,
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setRules(data.rules || [])
      } else {
        console.error("Failed to load bot rules:", data)
      }
    } catch (err) {
      console.error("loadRules error:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadRules()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (user && !user.bot) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-16 h-16 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Page Not Accessible</h3>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          Please contact your administrator to enable the Bot feature on your account.
        </p>
      </div>
    )
  }

  const resetForm = () => {
    setEditingRule(null)
    setForm({
      name: "",
      order: 0,
      isActive: true,
      triggerType: "contains",
      triggerText: "",
      responseText: "Hello {{name}}, welcome!",
    })
  }

  const onEdit = (rule) => {
    setEditingRule(rule)
    setForm({
      name: rule.name || "",
      order: rule.order || 0,
      isActive: rule.isActive ?? true,
      triggerType: rule.trigger?.type || "contains",
      triggerText: rule.trigger?.text || "",
      responseText: rule.response?.text || "",
    })
  }

  const onDelete = async (ruleId) => {
    if (!window.confirm("Delete this rule?")) return
    try {
      const res = await fetch(`${API_BASE}/api/bot/rules/${ruleId}`, {
        method: "DELETE",
        headers: authHeaders,
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setRules((prev) => prev.filter((r) => r._id !== ruleId))
      } else {
        alert(data.error || "Delete failed")
      }
    } catch (err) {
      console.error("delete rule error:", err)
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = {
        name: form.name,
        order: Number(form.order) || 0,
        isActive: !!form.isActive,
        trigger: {
          type: form.triggerType,
          text: form.triggerText,
          messageType: "text",
        },
        response: {
          type: "text",
          text: form.responseText,
        },
      }

      const method = editingRule ? "PUT" : "POST"
      const url = editingRule
        ? `${API_BASE}/api/bot/rules/${editingRule._id}`
        : `${API_BASE}/api/bot/rules`

      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload),
      })
      const data = await res.json()

      if (res.ok && data.success) {
        resetForm()
        loadRules()
      } else {
        alert(data.error || "Save failed")
      }
    } catch (err) {
      console.error("save rule error:", err)
    } finally {
      setSaving(false)
    }
  }

  // ------- UI -------

  return (
    <div className={`min-h-[calc(100vh-4rem)] p-4 md:p-8 transition-colors duration-500 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="mx-auto max-w-7xl">
        
        {/* Header Section */}
        <div className="mb-10 text-center md:text-left flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-500 mb-3 tracking-tight">
              Bot Intelligence
            </h1>
            <p className={`text-lg max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Train your WhatsApp bot to respond automatically. Set powerful triggers and dynamic responses to engage your audience 24/7.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
            <span className={`text-sm font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>Bot Engine Active</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: Form */}
          <div className="lg:col-span-5">
            <div className={`sticky top-6 rounded-[2rem] p-8 shadow-2xl backdrop-blur-xl border ${isDark ? 'bg-gray-800/80 border-gray-700/50 shadow-black/50' : 'bg-white/90 border-white shadow-blue-900/5'}`}>
              <div className="mb-6 flex items-center justify-between">
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {editingRule ? 'Refine Rule' : 'Create Rule'}
                </h2>
                {editingRule && (
                  <span className="animate-pulse text-xs rounded-full bg-blue-500/10 px-3 py-1 text-blue-500 border border-blue-500/20 font-semibold">
                    Editing Mode
                  </span>
                )}
              </div>

              <form onSubmit={onSubmit} className="space-y-6">
                
                {/* Rule Name */}
                <div className="group">
                  <label className={`block text-sm font-semibold mb-2 transition-colors ${isDark ? 'text-gray-300 group-focus-within:text-blue-400' : 'text-gray-700 group-focus-within:text-blue-600'}`}>
                    Rule Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g., Welcome Greeting"
                    className={`w-full rounded-xl border-2 px-4 py-3 text-sm transition-all duration-300 outline-none ${isDark ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'}`}
                    required
                  />
                </div>

                {/* Grid for Priority & Trigger Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="group">
                    <label className={`block text-sm font-semibold mb-2 transition-colors ${isDark ? 'text-gray-300 group-focus-within:text-blue-400' : 'text-gray-700 group-focus-within:text-blue-600'}`}>
                      Priority
                    </label>
                    <input
                      type="number"
                      value={form.order}
                      onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                      className={`w-full rounded-xl border-2 px-4 py-3 text-sm transition-all duration-300 outline-none ${isDark ? 'bg-gray-900/50 border-gray-700 text-white focus:border-blue-500 focus:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:bg-white'}`}
                    />
                  </div>
                  
                  <div className="group">
                    <label className={`block text-sm font-semibold mb-2 transition-colors ${isDark ? 'text-gray-300 group-focus-within:text-blue-400' : 'text-gray-700 group-focus-within:text-blue-600'}`}>
                      Trigger Type
                    </label>
                    <select
                      value={form.triggerType}
                      onChange={(e) => setForm((f) => ({ ...f, triggerType: e.target.value }))}
                      className={`w-full rounded-xl border-2 px-4 py-3 text-sm transition-all duration-300 outline-none cursor-pointer appearance-none ${isDark ? 'bg-gray-900/50 border-gray-700 text-white focus:border-blue-500 focus:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:bg-white'}`}
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='${isDark ? '%239ca3af' : '%236b7280'}' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                    >
                      {TRIGGER_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Trigger Keyword */}
                {form.triggerType !== "always" && (
                  <div className="group animate-in fade-in slide-in-from-top-2 duration-300">
                    <label className={`block text-sm font-semibold mb-2 transition-colors ${isDark ? 'text-gray-300 group-focus-within:text-blue-400' : 'text-gray-700 group-focus-within:text-blue-600'}`}>
                      Trigger Keyword
                    </label>
                    <input
                      type="text"
                      value={form.triggerText}
                      onChange={(e) => setForm((f) => ({ ...f, triggerText: e.target.value }))}
                      placeholder="e.g., pricing, hi, help"
                      className={`w-full rounded-xl border-2 px-4 py-3 text-sm transition-all duration-300 outline-none ${isDark ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'}`}
                      required
                    />
                  </div>
                )}

                {/* Response Text */}
                <div className="group">
                  <div className="flex justify-between items-end mb-2">
                    <label className={`block text-sm font-semibold transition-colors ${isDark ? 'text-gray-300 group-focus-within:text-blue-400' : 'text-gray-700 group-focus-within:text-blue-600'}`}>
                      Bot Response
                    </label>
                    <span className={`text-[10px] font-medium px-2 py-1 rounded-md ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                      Use {"{{name}}"} for variables
                    </span>
                  </div>
                  <textarea
                    value={form.responseText}
                    onChange={(e) => setForm((f) => ({ ...f, responseText: e.target.value }))}
                    placeholder="Hello {{name}}, how can we assist you today?"
                    className={`min-h-[120px] w-full rounded-xl border-2 px-4 py-3 text-sm transition-all duration-300 outline-none resize-y ${isDark ? 'bg-gray-900/50 border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:bg-gray-900' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:bg-white'}`}
                    required
                  />
                </div>

                {/* Active Toggle & Buttons */}
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
                  
                  {/* Modern Toggle */}
                  <label className="flex items-center cursor-pointer group">
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        className="sr-only" 
                        checked={form.isActive}
                        onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      />
                      <div className={`block w-12 h-7 rounded-full transition-colors duration-300 ${form.isActive ? 'bg-emerald-500' : (isDark ? 'bg-gray-700' : 'bg-gray-300')}`}></div>
                      <div className={`dot absolute left-1 top-1 bg-white w-5 h-5 rounded-full transition-transform duration-300 shadow-sm ${form.isActive ? 'transform translate-x-5' : ''}`}></div>
                    </div>
                    <span className={`ml-3 text-sm font-bold transition-colors ${form.isActive ? 'text-emerald-500' : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
                      {form.isActive ? 'Rule Active' : 'Rule Paused'}
                    </span>
                  </label>

                  <div className="flex gap-3 w-full sm:w-auto">
                    {editingRule && (
                      <button
                        type="button"
                        onClick={resetForm}
                        className={`flex-1 sm:flex-none rounded-xl px-5 py-3 text-sm font-bold transition-all duration-300 hover:-translate-y-1 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 sm:flex-none relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 hover:shadow-blue-500/50 hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0"
                    >
                      {saving ? (
                        <div className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                          Processing...
                        </div>
                      ) : (
                        editingRule ? "Update Rule" : "Deploy Rule"
                      )}
                    </button>
                  </div>
                </div>

              </form>
            </div>
          </div>

          {/* Right Column: Existing Rules Grid */}
          <div className="lg:col-span-7">
            
            <div className="flex items-center justify-between mb-6 px-2">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Active Logic Base
              </h2>
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600 shadow-sm'}`}>
                {rules.length} {rules.length === 1 ? 'Rule' : 'Rules'}
              </div>
            </div>

            {loading ? (
              <div className="grid sm:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`animate-pulse h-48 rounded-[2rem] ${isDark ? 'bg-gray-800/50' : 'bg-white/50'}`}></div>
                ))}
              </div>
            ) : rules.length === 0 ? (
              <div className={`flex flex-col items-center justify-center h-64 rounded-[2rem] border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-300 bg-white/50'}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
                </div>
                <p className={`text-lg font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No rules found</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Create your first automation rule to get started.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-6">
                {rules.map((r, idx) => (
                  <div 
                    key={r._id} 
                    className={`group relative rounded-[2rem] p-6 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${isDark ? 'bg-gray-800 border border-gray-700/50 hover:shadow-blue-900/20 hover:border-blue-500/30' : 'bg-white shadow-sm border border-transparent hover:shadow-blue-500/10 hover:border-blue-100'}`}
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1 pr-4">
                        <h3 className={`text-lg font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} title={r.name}>
                          {r.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                            Priority {r.order ?? 0}
                          </span>
                          <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${r.isActive ? (isDark ? 'bg-emerald-900/30 text-emerald-400' : 'bg-emerald-50 text-emerald-600') : (isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500')}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                            {r.isActive ? 'Active' : 'Paused'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Action buttons reveal on hover */}
                      <div className="flex opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300 gap-1 bg-white/10 backdrop-blur-md rounded-full p-1 border border-gray-200 dark:border-gray-700">
                        <button onClick={() => onEdit(r)} className="p-2 rounded-full hover:bg-blue-50 dark:hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 transition-colors" title="Edit">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path></svg>
                        </button>
                        <button onClick={() => onDelete(r._id)} className="p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-colors" title="Delete">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                        </button>
                      </div>
                    </div>

                    {/* Logic Flow Display */}
                    <div className={`p-4 rounded-xl space-y-3 ${isDark ? 'bg-gray-900/50 border border-gray-700/50' : 'bg-gray-50 border border-gray-100'}`}>
                      
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 p-1.5 rounded-lg ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                        </div>
                        <div>
                          <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>If Message</p>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {r.trigger?.type === 'always' ? (
                              <span className="italic text-purple-500 dark:text-purple-400">Always triggers (fallback)</span>
                            ) : (
                              <>
                                <span className="text-purple-500 dark:text-purple-400 mr-1">{r.trigger?.type?.replace('_', ' ')}</span>
                                <span className={`px-1.5 py-0.5 rounded font-mono text-xs ${isDark ? 'bg-gray-800 text-purple-300' : 'bg-white shadow-sm text-purple-600'}`}>"{r.trigger?.text}"</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className={`ml-3 w-0.5 h-4 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 p-1.5 rounded-lg ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                        </div>
                        <div className="flex-1">
                          <p className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Then Reply</p>
                          <p className={`text-sm font-medium leading-relaxed line-clamp-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {r.response?.text || "<no text>"}
                          </p>
                        </div>
                      </div>

                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

